<script setup>
import { computed } from 'vue';
import ReportMetricCard from './ReportMetricCard.vue';

const props = defineProps({
  metrics: {
    type: Object,
    required: true,
  },
  formatCurrency: {
    type: Function,
    required: true,
  },
});

const totalItems = computed(() => (props.metrics.totalItems || 0).toLocaleString());
const totalValue = computed(() => props.formatCurrency(props.metrics.totalValue || 0));
const totalContacts = computed(() => (props.metrics.contactMetrics?.totalContacts || 0).toLocaleString());
const totalConversations = computed(() => (props.metrics.activityMetrics?.itemsWithConversations || 0).toLocaleString());
const avgValue = computed(() => props.formatCurrency(props.metrics.averageValue || 0));
const itemsCreatedToday = computed(() => (props.metrics.stageMetrics?.itemsCreatedToday || 0).toLocaleString());
const itemsCreatedThisWeek = computed(() => (props.metrics.stageMetrics?.itemsCreatedThisWeek || 0).toLocaleString());
const itemsCreatedThisMonth = computed(() => (props.metrics.stageMetrics?.itemsCreatedThisMonth || 0).toLocaleString());
const totalOffers = computed(() => (props.metrics.stageMetrics?.totalOffers || 0).toLocaleString());
const avgOffersValue = computed(() => props.formatCurrency(props.metrics.stageMetrics?.avgOffersValue || 0));
const itemsWithChecklists = computed(() => (props.metrics.checklistMetrics?.itemsWithChecklists || 0).toLocaleString());
const checklistCompletionRate = computed(() => `${((props.metrics.checklistMetrics?.completionRate || 0) * 100).toFixed(1)}%`);
const itemsWithDeadline = computed(() => (props.metrics.stageMetrics?.itemsWithDeadline || 0).toLocaleString());
const itemsWithAttachments = computed(() => (props.metrics.activityMetrics?.itemsWithAttachments || 0).toLocaleString());
const itemsWithNotes = computed(() => (props.metrics.activityMetrics?.itemsWithNotes || 0).toLocaleString());
const itemsWithRescheduling = computed(() => (props.metrics.stageMetrics?.itemsWithRescheduling || 0).toLocaleString());
const itemsWithOffers = computed(() => (props.metrics.stageMetrics?.itemsWithOffers || 0).toLocaleString());
const avgActivitiesPerItem = computed(() => (props.metrics.activityMetrics?.averageActivitiesPerItem || 0).toLocaleString());
</script>

<template>
  <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mx-0 outline outline-1 outline-n-weak rounded-2xl bg-n-solid-1 px-4 py-4">
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.TOTAL_ITEMS')"
      :value="totalItems"
      :info-text="$t('REPORTS.KANBAN_METRICS.TOTAL_ITEMS_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.TOTAL_VALUE')"
      :value="totalValue"
      :info-text="$t('REPORTS.KANBAN_METRICS.TOTAL_VALUE_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.AVERAGE_VALUE')"
      :value="avgValue"
      :info-text="$t('REPORTS.KANBAN_METRICS.AVERAGE_VALUE_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_TODAY')"
      :value="itemsCreatedToday"
      :info-text="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_TODAY_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_THIS_WEEK')"
      :value="itemsCreatedThisWeek"
      :info-text="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_THIS_WEEK_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_THIS_MONTH')"
      :value="itemsCreatedThisMonth"
      :info-text="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_THIS_MONTH_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.TOTAL_OFFERS')"
      :value="totalOffers"
      :info-text="$t('REPORTS.KANBAN_METRICS.TOTAL_OFFERS_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.AVERAGE_OFFERS_VALUE')"
      :value="avgOffersValue"
      :info-text="$t('REPORTS.KANBAN_METRICS.AVERAGE_OFFERS_VALUE_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_CHECKLIST')"
      :value="itemsWithChecklists"
      :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_CHECKLIST_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.CHECKLIST_COMPLETION_RATE')"
      :value="checklistCompletionRate"
      :info-text="$t('REPORTS.KANBAN_METRICS.CHECKLIST_COMPLETION_RATE_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_DEADLINE')"
      :value="itemsWithDeadline"
      :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_DEADLINE_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_ATTACHMENTS')"
      :value="itemsWithAttachments"
      :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_ATTACHMENTS_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_NOTES')"
      :value="itemsWithNotes"
      :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_NOTES_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_CONVERSATION')"
      :value="totalConversations"
      :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_CONVERSATION_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_RESCHEDULING')"
      :value="itemsWithRescheduling"
      :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_RESCHEDULING_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_OFFERS')"
      :value="itemsWithOffers"
      :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_OFFERS_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.AVERAGE_ACTIVITIES_PER_ITEM')"
      :value="avgActivitiesPerItem"
      :info-text="$t('REPORTS.KANBAN_METRICS.AVERAGE_ACTIVITIES_PER_ITEM_INFO')"
    />
    <ReportMetricCard
      :label="$t('REPORTS.KANBAN_METRICS.TOTAL_CONTACTS')"
      :value="totalContacts"
      :info-text="$t('REPORTS.KANBAN_METRICS.TOTAL_CONTACTS_INFO')"
    />
  </div>
</template> 